import React, { useEffect, useId, useRef, useState } from 'react';
import { getApiUrl, getWebsiteURL, isNativeApp } from '../utils/platform';
import { openExternalUrl } from '../utils/openExternal';
import { useTheme } from '../ThemeContext';
import { POLICY_EFFECTIVE_DATE, POLICY_LAST_UPDATED, followPolicyLink } from '../utils/legal';
import { parseLegalMarkdown, publishableLegalMarkdown } from '../utils/legalMarkdown';
import docs from '../legalDocs/bundle.json';
import { isFloridaHome } from '../shared/homeRegion';
import {
  BEFORE_YOU_HIRE,
  DPA_PATH,
  LICENSE_LOOKUP_URL,
  beforeYouHireNotice,
  licensePrompt,
  VENDOR_ATTESTATION,
  VENDOR_ATTESTATION_TEXT,
  VENDOR_DISCLAIMER_LINK,
  VENDOR_DISCLAIMER_PATH,
  VENDOR_SUBMISSIONS_PATH,
  attestationAccepted,
  contactAttempt,
  providerListNotice,
  vendorListHeader,
  vendorListHeaderParts,
} from '../shared/vendorConsent';

function termsSection103Blocks() {
  const blocks = parseLegalMarkdown(publishableLegalMarkdown(docs.terms, POLICY_EFFECTIVE_DATE, POLICY_LAST_UPDATED));
  const start = blocks.findIndex((block) => block.id === 'vendor-disclaimer');
  if (start < 0) return [];
  const end = blocks.findIndex((block, index) => index > start && (block.type === 'h2' || block.type === 'h3'));
  return blocks.slice(start, end < 0 ? blocks.length : end);
}

const TERMS_SECTION_103 = termsSection103Blocks();

function Inline({ nodes }) {
  return (nodes || []).map((node, index) => {
    if (typeof node === 'string') return <React.Fragment key={index}>{node}</React.Fragment>;
    if (node.type === 'bold') return <strong key={index}><Inline nodes={node.children} /></strong>;
    if (node.type === 'italic') return <em key={index}><Inline nodes={node.children} /></em>;
    if (node.type === 'code') return <code key={index}>{node.text}</code>;
    if (node.type === 'link') return <Inline key={index} nodes={node.children} />;
    return null;
  });
}

export function VendorDisclaimerControl({ home }) {
  const { colors } = useTheme();
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const florida = isFloridaHome(home);
  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((value) => !value)}
        style={{
          background: 'transparent',
          border: 0,
          padding: 0,
          color: colors.primary,
          font: 'inherit',
          fontWeight: 650,
          textDecoration: 'underline',
          cursor: 'pointer',
        }}
      >
        {VENDOR_DISCLAIMER_LINK} ▾
      </button>
      <div
        id={panelId}
        role="region"
        aria-label="Terms section 10.3"
        hidden={!open}
        style={{
          display: open ? 'block' : 'none',
          marginTop: '0.5rem',
          padding: '0.75rem',
          borderRadius: '0.5rem',
          background: colors.surface,
          color: colors.text,
          border: `1px solid ${colors.border}`,
        }}
      >
        {TERMS_SECTION_103.map((block, index) => (
          <p key={index} style={{ margin: index === TERMS_SECTION_103.length - 1 ? 0 : '0 0 0.5rem', color: colors.text, fontWeight: block.type === 'h3' ? 700 : 400 }}>
            <Inline nodes={block.children} />
          </p>
        ))}
        <p style={{ margin: '0.75rem 0 0' }}>
          {florida ? (
            <a href={LICENSE_LOOKUP_URL} target="_blank" rel="noopener noreferrer">
              myfloridalicense.com
            </a>
          ) : (
            <LicensePrompt home={home} />
          )}
          {' '}
          <a
            href={VENDOR_DISCLAIMER_PATH}
            onClick={(event) => {
              if (isNativeApp()) {
                openConsentDocument(event, VENDOR_DISCLAIMER_PATH);
                return;
              }
              followPolicyLink(event);
            }}
            style={{ color: colors.primary, fontWeight: 650 }}
          >
            Read full Terms §10.3
          </a>
        </p>
      </div>
    </div>
  );
}

function LicensePrompt({ home }) {
  const license = licensePrompt(home);
  if (!license.href) return <span>{license.label}</span>;
  return (
    <a href={license.href} target="_blank" rel="noopener noreferrer">
      {license.label}
    </a>
  );
}

export function ProviderListNotice({
  agentName,
  plan,
  teamId,
  orgName,
  subject,
  style,
  home,
}) {
  const notice = providerListNotice({ agentName, plan, teamId, orgName, subject });
  return (
    <div style={style}>
      <p style={{ margin: '0 0 0.5rem' }}>{notice}</p>
      <VendorDisclaimerControl home={home} />
    </div>
  );
}

export function VendorCardFootnote({ agentName, style, home, plan, teamId, orgName, subject }) {
  return (
    <ProviderListNotice
      agentName={agentName}
      plan={plan}
      teamId={teamId}
      orgName={orgName}
      subject={subject}
      style={style}
      home={home}
    />
  );
}

function absoluteDocumentUrl(href) {
  if (/^https?:\/\//i.test(href)) return href;
  const path = href.startsWith('/') ? href : `/${href}`;
  return `${getWebsiteURL()}${path}`;
}

function openConsentDocument(event, href) {
  event.stopPropagation();
  if (!isNativeApp()) return;
  event.preventDefault();
  openExternalUrl(absoluteDocumentUrl(href));
}

function linkedNotice(text, links) {
  const nodes = [];
  let rest = text;
  links.forEach((link) => {
    const at = rest.indexOf(link.label);
    if (at < 0) return;
    if (at > 0) nodes.push(rest.slice(0, at));
    const blank = link.external || link.blank;
    nodes.push(
      <a
        key={link.label}
        href={link.href}
        target={blank ? '_blank' : undefined}
        rel={blank ? 'noopener noreferrer' : undefined}
        onClick={blank ? (event) => openConsentDocument(event, link.href) : undefined}
        style={{ color: '#59c4c4', fontWeight: 650 }}
      >
        {link.label}
      </a>
    );
    rest = rest.slice(at + link.label.length);
  });
  if (rest) nodes.push(rest);
  return nodes;
}

export function BeforeYouHireModal({ open, saving, error, onAccept, onDismiss, home }) {
  if (!open) return null;
  return (
    <div
      role="presentation"
      onClick={onDismiss}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="before-you-hire-title"
        onClick={(event) => event.stopPropagation()}
        style={{
          background: '#fff',
          color: '#1f2937',
          borderRadius: '0.75rem',
          maxWidth: '32rem',
          width: '100%',
          padding: '1.5rem',
          boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
        }}
      >
        <p id="before-you-hire-title" style={{ margin: 0, fontSize: '0.95rem', lineHeight: 1.5 }}>
          {linkedNotice(beforeYouHireNotice(home), [
            ...(isFloridaHome(home) ? [{ label: 'myfloridalicense.com', href: LICENSE_LOOKUP_URL, external: true }] : []),
            { label: '/terms#vendor-disclaimer', href: VENDOR_DISCLAIMER_PATH },
          ])}
        </p>
        {error && (
          <p style={{ color: '#b91c1c', fontSize: '0.85rem', margin: '0.75rem 0 0 0' }}>{error}</p>
        )}
        <div style={{ marginTop: '1.25rem' }}>
          <button type="button" onClick={onAccept} disabled={saving} style={primaryButton}>
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

const primaryButton = {
  background: '#0f766e',
  color: '#fff',
  border: 'none',
  borderRadius: '0.5rem',
  padding: '0.6rem 1rem',
  fontWeight: 600,
  cursor: 'pointer',
};

export function VendorAttestationField({ checked, onChange, disabled, color }) {
  return (
    <label style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', marginTop: '1rem', fontSize: '16px', lineHeight: 1.45, color: color || 'inherit' }}>
      <input
        type="checkbox"
        checked={checked === true}
        disabled={disabled}
        required
        onChange={(event) => onChange(event.target.checked)}
        style={{ marginTop: '0.2rem', width: '1.1rem', height: '1.1rem', flexShrink: 0 }}
      />
      <span>
        {VENDOR_ATTESTATION_TEXT}
        {' ('}
        <a
          href={VENDOR_SUBMISSIONS_PATH}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(event) => openConsentDocument(event, VENDOR_SUBMISSIONS_PATH)}
          style={{ color: '#59c4c4', fontWeight: 650 }}
        >
          Terms §13.4
        </a>
        {'; '}
        <a
          href={DPA_PATH}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(event) => openConsentDocument(event, DPA_PATH)}
          style={{ color: '#59c4c4', fontWeight: 650 }}
        >
          DPA §2A
        </a>
        ).
      </span>
    </label>
  );
}

export function HomeownerVendorListNotice({ agentName, plan, teamId, orgName, subject, style, home }) {
  return (
    <ProviderListNotice
      agentName={agentName}
      plan={plan}
      teamId={teamId}
      orgName={orgName}
      subject={subject}
      style={style}
      home={home}
    />
  );
}

export function useVendorHireGate(loadStatus, recordAcceptance) {
  const loadRef = useRef(loadStatus);
  const recordRef = useRef(recordAcceptance);
  loadRef.current = loadStatus;
  recordRef.current = recordAcceptance;
  const [acknowledged, setAcknowledged] = useState(false);
  const [pending, setPending] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancel = false;
    Promise.resolve()
      .then(() => loadRef.current())
      .then((accepted) => {
        if (!cancel) setAcknowledged(accepted === true);
      })
      .catch(() => {});
    return () => {
      cancel = true;
    };
  }, []);

  function requestContact(event, contact) {
    const decision = contactAttempt({
      acknowledged,
      kind: contact.kind,
    });
    if (decision.allow) {
      if (contact.onAllowed) contact.onAllowed();
      return;
    }
    if (!decision.showModal) return;
    event.preventDefault();
    setError('');
    setPending(contact);
  }

  function dismiss() {
    if (saving) return;
    setPending(null);
    setError('');
  }

  async function accept() {
    if (!pending || saving) return;
    setSaving(true);
    setError('');
    try {
      await recordRef.current(pending);
      setAcknowledged(true);
      const next = pending;
      setPending(null);
      if (next.onAllowed) next.onAllowed();
      if (next.href) {
        if (next.href) openExternalUrl(next.href);
      }
    } catch (err) {
      setError(err.message || 'Could not save your acknowledgment.');
    } finally {
      setSaving(false);
    }
  }

  return {
    acknowledged,
    pending,
    saving,
    error,
    requestContact,
    accept,
    dismiss,
  };
}

export async function postVendorConsent(headers, body) {
  const response = await fetch(getApiUrl('/.netlify/functions/vendor-consent'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(headers || {}) },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || 'Could not save consent');
  }
  return data;
}

export { BEFORE_YOU_HIRE, VENDOR_ATTESTATION, attestationAccepted, vendorListHeader, vendorListHeaderParts };
